import { useMediaQuery, useTheme, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { thousandSeparator } from "./utils/thousandSeparator";
import { MicroRingChart } from "./MicroRingChart";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  tableRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    width: "100%",
    padding: theme.spacing(2, 3, 2, 3),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },
  },

  tableCell: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "none",
    textWrap: "nowrap",
    width: "20%",

    [theme.breakpoints.down("sm")]: {
      width: "33%",
    },
  },

  targetTableCell: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "none",
    textWrap: "nowrap",
    width: "20%",
  },

  halfWidth: {
    width: "50%",
  },
}));

export interface ClientNutritionTableRowProps {
  color: string;
  value: number;
  target?: number;
  unit?: string;
  disableTag?: boolean;
  chart?: React.ReactNode;
  title: string;
  showAverageIntake?: boolean;
  isToday?: boolean;
  compactUnits?: boolean;
  hideTarget?: boolean;
}

export default function ClientNutritionTableRow(
  props: ClientNutritionTableRowProps,
) {
  const {
    title,
    showAverageIntake,
    value,
    target,
    color,
    isToday,
    compactUnits,
    unit = "g",
    hideTarget,
    chart,
  } = props;
  const s = useStyles();
  const theme = useTheme();

  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const remaining =
    target !== undefined
      ? Math.round((target - value || 0) * 10) / 10
      : undefined;

  const averageIntake =
    target !== undefined ? Math.round((value / target) * 100) : 100;

  return (
    <Box className={s.tableRow}>
      <Box className={clsx(s.tableCell, hideTarget && s.halfWidth)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: -2 }}>
          <Box sx={{ width: "max-content" }}>
            {chart || (
              <MicroRingChart
                value={value ? (target ? value / target : 1) : 0}
                color={color}
              />
            )}
          </Box>
          <Typography variant="subtitle1">{title}</Typography>
          {!hideTarget && !compactUnits && (
            <Typography
              sx={{
                marginTop: 0.5,
                marginLeft: 0.5,
                textWrap: "nowrap",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
              }}
              fontWeight="bold"
              variant="subtitle2"
              color={color}
            >
              {value !== undefined ? thousandSeparator(value) : "-"} {unit}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={!hideTarget ? { mr: { sm: 2, md: 0 } } : { ml: { sm: 0, md: 1 } }}
        className={clsx(s.tableCell, hideTarget && s.halfWidth)}
      >
        {hideTarget ? (
          <Typography variant="subtitle1" fontWeight="regular" align="left">
            {thousandSeparator(value)} {unit}
          </Typography>
        ) : remaining !== undefined ? (
          <Typography variant="subtitle1" align="right">
            {showAverageIntake
              ? `${averageIntake}%`
              : `${thousandSeparator(Math.abs(remaining))} ${smUp || !compactUnits ? unit : ""}`}
            <Typography
              variant="caption"
              color="textSecondary"
              ml={0.5}
              fontWeight="bold"
            >
              {!showAverageIntake && (remaining < 0 ? "over" : "left")}
            </Typography>
          </Typography>
        ) : (
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="textSecondary"
            align="right"
          >
            {value !== undefined ? "no target" : `-- ${unit}`}
          </Typography>
        )}
      </Box>
      {!hideTarget && (
        <Box className={s.targetTableCell}>
          {target !== undefined ? (
            <Typography variant="subtitle2" align="right">
              {thousandSeparator(Math.round(target * 10) / 10)}
              {smUp || !compactUnits ? ` ${unit}` : " "}
              {!smUp && compactUnits && (
                <Typography
                  align="right"
                  variant="subtitle2"
                  sx={{ height: 0 }}
                  fontWeight="bold"
                  color="textSecondary"
                >
                  {unit}
                </Typography>
              )}
            </Typography>
          ) : (
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="textSecondary"
              align="right"
              sx={{ textWrap: { xs: "wrap", md: "nowrap" } }}
            >
              {value !== undefined ? "-" : `-- ${unit}`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
